import BigNumber from 'bignumber.js';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { GetOrderBookResp, PriceSizeTuple } from '#/api/order-book';
import { useTradeActiveMarketStoreView } from '#/features/perpetuals/active-market-context';
import * as MarketFn from '#/features/market/functions';
import { FeatureFlags } from '#/features/feature-flags';
import { OrderBookNotification } from '#/api/ws/ws';
import { Market } from '#/api/markets';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';

import {
  calculateSpread,
  isOrderBookEmpty,
  OrderBookSpread,
  parseAsks,
  parseBids,
  parseIds,
} from './functions';

const selectId = ([price]: PriceSizeTuple) => price.toString();

const asksAdapter = createEntityAdapter<PriceSizeTuple>({
  selectId,
  sortComparer: ([price1], [price2]) => price2.comparedTo(price1),
});
const asksSelectors = asksAdapter.getSelectors();

const bidsAdapter = createEntityAdapter<PriceSizeTuple>({
  selectId,
  sortComparer: ([price1], [price2]) => price2.comparedTo(price1),
});
const bidsSelectors = bidsAdapter.getSelectors();

const INITIAL_STATE: OrderBookState = {
  status: 'loading',
  loadRetryCount: 0,
  error: '',
  orderBook: {
    asks: asksAdapter.getInitialState(),
    bids: bidsAdapter.getInitialState(),
  },
};

interface OrderBookState extends LiveConnectionBaseState {
  readonly orderBook: {
    asks: EntityState<PriceSizeTuple>;
    bids: EntityState<PriceSizeTuple>;
  };
}

export interface OrderBookItemView {
  readonly rawPrice: string;
  readonly price: string;
  readonly size: string;

  // Derived
  readonly sizeRelativePercentage: string;
  readonly runningSize: string;
  readonly runningSizeRelativePercentage: string;
}

interface OrderBookSpreadView {
  readonly relative: string;
}

export interface OrderBookView extends Omit<OrderBookState, 'orderBook'> {
  readonly orderBook: {
    readonly asks: OrderBookItemView[];
    readonly bids: OrderBookItemView[];
  };
  readonly spread: OrderBookSpreadView;
  readonly baseCurrency: string;
}

export const [useOrderBookView, OrderBookViewCtx] =
  createSafeContext<OrderBookView>('OrderBookView');

interface ProviderProps {
  readonly children: ReactNode;
  readonly depth: number;
}

export function OrderBookProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const { activeMarket } = useTradeActiveMarketStoreView();

  const view = useMemo(
    () => createView(state, activeMarket, props.depth),
    [state, activeMarket, props.depth],
  );

  useOrderBookFetch(setState);

  return <OrderBookViewCtx value={view}>{props.children}</OrderBookViewCtx>;
}

const emptyView = (state: OrderBookState, depth: number): OrderBookView => ({
  ...state,
  orderBook: {
    asks: new Array<OrderBookItemView>(depth).fill(emptyOrderBookItemView),
    bids: new Array<OrderBookItemView>(depth).fill(emptyOrderBookItemView),
  },
  spread: {
    relative: '-',
  },
  baseCurrency: '',
});

function createView(
  state: OrderBookState,
  activeMarket: Market | null,
  depth: number,
): OrderBookView {
  const orderBook = {
    asks: asksSelectors.selectAll(state.orderBook.asks),
    bids: bidsSelectors.selectAll(state.orderBook.bids),
  };

  if (activeMarket == null || isOrderBookEmpty(orderBook)) {
    return emptyView(state, depth);
  }

  return {
    ...state,
    orderBook: {
      asks: asksView(orderBook.asks, activeMarket, depth),
      bids: bidsView(orderBook.bids, activeMarket, depth),
    },
    spread: spreadView(calculateSpread(orderBook)),
    baseCurrency: activeMarket.base_currency,
  };
}

function asksView(
  asks: readonly PriceSizeTuple[],
  market: Market,
  depth: number,
): OrderBookItemView[] {
  return sideOrdersView(asks.slice().reverse(), market, depth).reverse();
}

function bidsView(
  bids: readonly PriceSizeTuple[],
  market: Market,
  depth: number,
): OrderBookItemView[] {
  return sideOrdersView(bids, market, depth);
}

/**
 * @param orders orders ordered from best to worst
 * @param market orders' market
 * @param depth number of rows to display
 */
function sideOrdersView(
  orders: readonly PriceSizeTuple[],
  market: Market,
  depth: number,
): OrderBookItemView[] {
  const totalSize = orders.reduce(
    (acc, [_, size]) => acc.plus(size),
    new BigNumber(0),
  );

  let runningSize = new BigNumber(0);
  const items: OrderBookItemView[] = [];

  for (const [price, size] of orders.slice(0, depth)) {
    runningSize = runningSize.plus(size);

    items.push(
      itemView({
        market,
        price,
        size,
        totalSize,
        runningSize,
      }),
    );
  }

  const missingDepthLength = Math.max(depth - orders.length, 0);
  for (let i = 0; i < missingDepthLength; i++) {
    items.push(emptyOrderBookItemView);
  }

  return items;
}

interface ItemViewParams {
  readonly market: Market;
  readonly price: BigNumber;
  readonly size: BigNumber;
  readonly totalSize: BigNumber;
  readonly runningSize: BigNumber;
}

function itemView({
  market,
  price,
  size,
  totalSize,
  runningSize,
}: ItemViewParams): OrderBookItemView {
  return {
    rawPrice: price.toString(),
    price: MarketFn.formatPrice(market, price),
    size: MarketFn.formatSize(market, size),
    sizeRelativePercentage: size
      .dividedBy(totalSize)
      .multipliedBy(100)
      .toFixed(0),
    runningSize: MarketFn.formatSize(market, runningSize),
    runningSizeRelativePercentage: runningSize
      .dividedBy(totalSize)
      .multipliedBy(100)
      .toFixed(0),
  };
}

const emptyOrderBookItemView: OrderBookItemView = {
  rawPrice: '',
  price: '-',
  size: '-',
  sizeRelativePercentage: '0',
  runningSize: '-',
  runningSizeRelativePercentage: '0',
};

function spreadView(spread: OrderBookSpread | null): OrderBookSpreadView {
  if (spread == null)
    return {
      relative: '-',
    };

  return {
    relative: `${spread.relative.toFixed(2)}%`,
  };
}

function useOrderBookFetch(setState: Dispatch<SetStateAction<OrderBookState>>) {
  const { activeMarket } = useTradeActiveMarketStoreView();
  const isReady = activeMarket != null;

  const snapshotsEnabled = FeatureFlags.getBooleanValue(
    'order_book-100ms-snapshots-enabled',
    false,
  );

  const channel = isReady
    ? snapshotsEnabled
      ? `order_book.${activeMarket.symbol}.snapshot@15@100ms`
      : `order_book.${activeMarket.symbol}.deltas`
    : '';

  /**
   * WIP: OB no longer uses `REST` API to get snapshot. Refactor this code to use `WS` only
   * to achieve that we would probably need to update how `useWSListener` handles `status`
   * @see https://github.com/tradeparadigm/mono/pull/13538#discussion_r1234190812
   */
  const fetchAll = useCallback(
    async () => ({
      ok: true as const,
      status: 200,
      data: {
        asks: [],
        bids: [],
      },
    }),
    [],
  );

  const setAll = useCallback(
    (data: GetOrderBookResp) => {
      setState((state) => ({
        ...state,
        orderBook: {
          asks: asksAdapter.setAll(state.orderBook.asks, data.asks),
          bids: bidsAdapter.setAll(state.orderBook.bids, data.bids),
        },
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      orderBook: {
        asks: asksAdapter.removeAll(state.orderBook.asks),
        bids: bidsAdapter.removeAll(state.orderBook.bids),
      },
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: OrderBookNotification) => {
      setState((state) => {
        const insertAsks = parseAsks(notif.data.inserts);
        const insertBids = parseBids(notif.data.inserts);

        const deleteAskIds = parseIds(notif.data.deletes);
        const deleteBidIds = parseIds(notif.data.deletes);

        const updateAsks = parseAsks(notif.data.updates);
        const updateBids = parseBids(notif.data.updates);

        switch (notif.data.update_type) {
          case 's': {
            // fully replace orderbook
            const asks = asksAdapter.setAll(state.orderBook.asks, insertAsks);
            const bids = bidsAdapter.setAll(state.orderBook.bids, insertBids);

            return {
              ...state,
              orderBook: {
                asks,
                bids,
              },
            };
          }
          case 'd': {
            // delete
            let asks = asksAdapter.removeMany(
              state.orderBook.asks,
              deleteAskIds,
            );
            let bids = bidsAdapter.removeMany(
              state.orderBook.bids,
              deleteBidIds,
            );

            // update existing
            asks = asksAdapter.upsertMany(asks, updateAsks);
            bids = bidsAdapter.upsertMany(bids, updateBids);

            // insert newly added
            asks = asksAdapter.upsertMany(asks, insertAsks);
            bids = bidsAdapter.upsertMany(bids, insertBids);

            return {
              ...state,
              orderBook: {
                asks,
                bids,
              },
            };
          }
          // no default
        }
      });
    },
    [setState],
  );

  useLiveConnection<GetOrderBookResp, OrderBookState, OrderBookNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
